// Dependencies
import React, { ReactElement, ReactNode } from 'react';

// Scrollbar
import 'simplebar/src/simplebar.css';

// Iconify
import { loadIcons } from '@iconify/react';
import iconifyIcons from 'src/assets/iconifyIcons';

// Fonts
import '@fontsource/raleway'; // Defaults to weight 400

// Lazy Load Images
import 'react-lazy-load-image-component/src/effects/blur.css';
import 'react-lazy-load-image-component/src/effects/opacity.css';
import 'react-lazy-load-image-component/src/effects/black-and-white.css';

// Redux state management
import { Provider as ReduxProvider } from 'react-redux';
import { store } from 'src/store/store';

// Next
import { NextPage } from 'next';
import Head from 'next/head';
import { AppProps } from 'next/app';
import { useRouter } from 'next/router';

// I18n
import { appWithTranslation } from 'next-i18next';

// Material UI Theme
import ThemeProvider from '../theme';

// Motion
import MotionLazyContainer from '../components/animate/MotionLazyContainer';

// Material UI Date Picker
import { LocalizationProvider, LicenseInfo } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

// Timing
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import updateLocale from 'dayjs/plugin/updateLocale';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/ru';
import 'dayjs/locale/ar';

// Layouts
import ThemeRtlLayout from 'src/layouts/ThemeRtlLayout';

// API
import { SocketProvider } from 'src/socket/socketContext';
import { setLocaleLanguage } from 'src/store/slices/auth';

// Set dayjs locale
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(localeData);
dayjs.extend(localizedFormat);
dayjs.extend(updateLocale);
dayjs.extend(duration);
dayjs.extend(relativeTime);

dayjs.updateLocale('ar', {
  preparse: function (string: string) {
    return string
      .replace(/\u200f/g, '')
      .replace(/[١٢٣٤٥٦٧٨٩٠]/g, function (match: string) {
        const numberMap = {
          '١': '1',
          '٢': '2',
          '٣': '3',
          '٤': '4',
          '٥': '5',
          '٦': '6',
          '٧': '7',
          '٨': '8',
          '٩': '9',
          '٠': '0',
        };
        return numberMap[match as keyof typeof numberMap];
      })
      .replace(/،/g, ',');
  },
  postformat: function (string: string) {
    return string
      .replace(/\d/g, function (match: string) {
        const symbolMap = {
          '1': '1',
          '2': '2',
          '3': '3',
          '4': '4',
          '5': '5',
          '6': '6',
          '7': '7',
          '8': '8',
          '9': '9',
          '0': '0',
        };
        return symbolMap[match as keyof typeof symbolMap];
      })
      .replace(/,/g, '،');
  },
});

// Set MUI X Date Picker license key
LicenseInfo.setLicenseKey(
  '89bc5203780bbc4bfd7d904da0675bdcT1JERVI6c2tuZGFrc25kYXNkLEVYUElSWT0xNzg2MTA2NjI4MDAwLEtFWVZFUlNJT049MQ=='
);

// ----------------------------------------------------------------------
type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode;
};

interface MyAppProps extends AppProps {
  Component: NextPageWithLayout;
}

function MyApp({ Component, pageProps }: MyAppProps) {
  // ===================================
  // State
  const [isMounted, setIsMounted] = React.useState(false);
  // ===================================
  // Layout
  const getLayout = Component.getLayout ?? ((page) => page);

  // ===================================
  // I18n
  const router = useRouter();

  // ===================================
  // Load iconify icons
  React.useEffect(() => {
    setIsMounted(true);
    loadIcons(Object.keys(iconifyIcons));
  }, []);

  React.useEffect(() => {
    store.dispatch(setLocaleLanguage(router.locale));
  }, [router.locale]);

  // ===================================
  // Display attention message in console
  React.useEffect(() => {
    console.log(
      '%cJsur Capital',
      'color: #078DEE; font-size: 30px; font-weight: bold; text-shadow: 2px 2px 4px rgba(154, 154, 154, 0.5); background-color: #f0f0f0; padding: 10px; border-radius: 5px;'
    );
    console.log(
      '%cATTENTION!',
      'color: red; font-size: 50px; font-weight: bold; text-shadow: 2px 2px 4px rgba(0,0,0,0.5); background-color: yellow; padding: 10px; border-radius: 5px;'
    );
    console.log(
      '%cThis is a developer tool. Pasting anything here could compromise your account.',
      'color: yellow; font-size: 20px; background-color: black; padding: 10px; border-radius: 5px;'
    );
    console.log(
      '%cIf someone told you to copy/paste something here, it is a scam and they are trying to gain access to your account.',
      'font-size: 16px; color: white; background-color: red; padding: 10px; border-radius: 5px;'
    );
  }, []);

  // ===================================
  // Set time locale
  dayjs.locale(router?.locale || 'en');

  // ===================================
  // Render
  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0"
        />
      </Head>

      <ReduxProvider store={store}>
        <SocketProvider>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={router.locale || 'en'}>
            <MotionLazyContainer>
              <ThemeProvider>
                <ThemeRtlLayout>
                  {isMounted ? getLayout(<Component {...pageProps} />) : null}
                </ThemeRtlLayout>
              </ThemeProvider>
            </MotionLazyContainer>
          </LocalizationProvider>
        </SocketProvider>
      </ReduxProvider>
    </>
  );
}

export default appWithTranslation(MyApp);
